import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Building2,
  Cloud,
  Globe,
  Shield,
  Layers,
  Server,
  Monitor,
  Box,
  Trash2,
  RefreshCw,
  Download,
  Maximize,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Node } from '@xyflow/react';

interface LayoutToolsPaneProps {
  selectedNode: Node | null;
  selectedEdgeId: string | null;
  isLayouting: boolean;
  onAddSite: () => void;
  onAddCloudService: () => void;
  onAddZone: () => void;
  onAddSystem: () => void;
  onAddEndpoint: () => void;
  onAddCloudResource: () => void;
  onAddInternet: () => void;
  onAddDMZ: () => void;
  onGenerateFromBrowser: () => void;
  onDeleteEdge: () => void;
  onFitView: () => void;
  onExportPNG: () => void;
}

// Hierarchy rules for element creation
type ParentType = 'none' | 'site' | 'zone' | 'system' | 'cloudService' | 'cloudGroup';

function getSelectedParentType(node: Node | null): ParentType {
  if (!node) return 'none';
  
  const type = node.type;
  if (type === 'siteGroup') return 'site';
  if (type === 'zoneGroup') return 'zone';
  if (type === 'systemGroup') return 'system';
  if (type === 'cloudGroup') {
    // Check if it's the parent cloud container or individual service
    return node.id === 'cloudServices' ? 'cloudGroup' : 'cloudService';
  }
  return 'none';
}

function getSelectedLabel(node: Node | null): string {
  if (!node) return 'Nothing selected';
  const data = node.data as Record<string, unknown> | undefined;
  const label = data?.label;
  return typeof label === 'string' ? label : (node.type || 'Unknown');
}

export function LayoutToolsPane({
  selectedNode,
  selectedEdgeId,
  isLayouting,
  onAddSite,
  onAddCloudService,
  onAddZone,
  onAddSystem,
  onAddEndpoint,
  onAddCloudResource,
  onAddInternet,
  onAddDMZ,
  onGenerateFromBrowser,
  onDeleteEdge,
  onFitView,
  onExportPNG,
}: LayoutToolsPaneProps) {
  const parentType = getSelectedParentType(selectedNode);
  const selectedLabel = getSelectedLabel(selectedNode);
  
  // Determine which buttons should be enabled based on hierarchy rules
  const canAddZone = parentType === 'site';
  const canAddSystem = parentType === 'zone';
  const canAddEndpoint = parentType === 'system';
  const canAddCloudResource = parentType === 'cloudService';
  const canAddInternet = parentType === 'site' || parentType === 'zone' || parentType === 'system';
  const canAddDMZ = parentType === 'site' || parentType === 'zone' || parentType === 'system' || parentType === 'cloudService';

  return (
    <div className="w-64 bg-background border-r border-border p-4 space-y-4 overflow-y-auto">
      {/* Selection Context */}
      <div className="p-2 rounded-md bg-muted/50 border border-border">
        <div className="text-xs text-muted-foreground mb-1">Selected</div>
        <div className="font-medium text-sm truncate">
          {selectedNode ? (
            <span className="flex items-center gap-2">
              {parentType === 'site' && <Building2 className="h-3.5 w-3.5 text-slate-500" />}
              {parentType === 'zone' && <Layers className="h-3.5 w-3.5 text-blue-500" />}
              {parentType === 'system' && <Server className="h-3.5 w-3.5 text-green-500" />}
              {(parentType === 'cloudService' || parentType === 'cloudGroup') && <Cloud className="h-3.5 w-3.5 text-purple-500" />}
              {selectedLabel}
            </span>
          ) : (
            <span className="text-muted-foreground">Nothing selected</span>
          )}
        </div>
      </div>

      {/* Create Elements */}
      <div>
        <h3 className="font-semibold text-xs uppercase text-muted-foreground mb-2">Create Elements</h3>
        <div className="space-y-1">
          {/* Top-level elements - always enabled */}
          <Button 
            onClick={onAddSite}
            variant="ghost" 
            className="w-full justify-start h-8 text-sm"
            size="sm"
          >
            <Building2 className="h-4 w-4 mr-2 text-slate-500" />
            Add Site
          </Button>
          
          <Button 
            onClick={onAddCloudService}
            variant="ghost" 
            className="w-full justify-start h-8 text-sm"
            size="sm"
          >
            <Cloud className="h-4 w-4 mr-2 text-purple-500" />
            Add Cloud Service
          </Button>
          
          <Separator className="my-2" />
          
          {/* Hierarchy-dependent elements */}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button 
                    onClick={onAddZone}
                    variant="ghost" 
                    className={cn(
                      "w-full justify-start h-8 text-sm",
                      !canAddZone && "opacity-50 cursor-not-allowed"
                    )}
                    size="sm"
                    disabled={!canAddZone}
                  >
                    <Layers className="h-4 w-4 mr-2 text-blue-500" />
                    Add Zone
                    {canAddZone && <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">✓</Badge>}
                  </Button>
                </div>
              </TooltipTrigger>
              {!canAddZone && (
                <TooltipContent side="right">
                  <p>Select a Site first</p>
                </TooltipContent>
              )}
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button 
                    onClick={onAddSystem}
                    variant="ghost" 
                    className={cn(
                      "w-full justify-start h-8 text-sm",
                      !canAddSystem && "opacity-50 cursor-not-allowed"
                    )}
                    size="sm"
                    disabled={!canAddSystem}
                  >
                    <Server className="h-4 w-4 mr-2 text-green-500" />
                    Add System
                    {canAddSystem && <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">✓</Badge>}
                  </Button>
                </div>
              </TooltipTrigger>
              {!canAddSystem && (
                <TooltipContent side="right">
                  <p>Select a Zone first</p>
                </TooltipContent>
              )}
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button 
                    onClick={onAddEndpoint}
                    variant="ghost" 
                    className={cn(
                      "w-full justify-start h-8 text-sm",
                      !canAddEndpoint && "opacity-50 cursor-not-allowed"
                    )}
                    size="sm"
                    disabled={!canAddEndpoint}
                  >
                    <Monitor className="h-4 w-4 mr-2 text-emerald-500" />
                    Add Endpoint
                    {canAddEndpoint && <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">✓</Badge>}
                  </Button>
                </div>
              </TooltipTrigger>
              {!canAddEndpoint && (
                <TooltipContent side="right">
                  <p>Select a System first</p>
                </TooltipContent>
              )}
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button 
                    onClick={onAddCloudResource}
                    variant="ghost" 
                    className={cn(
                      "w-full justify-start h-8 text-sm",
                      !canAddCloudResource && "opacity-50 cursor-not-allowed"
                    )}
                    size="sm"
                    disabled={!canAddCloudResource}
                  >
                    <Box className="h-4 w-4 mr-2 text-purple-400" />
                    Add Cloud Resource
                    {canAddCloudResource && <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">✓</Badge>}
                  </Button>
                </div>
              </TooltipTrigger>
              {!canAddCloudResource && (
                <TooltipContent side="right">
                  <p>Select a Cloud Service first</p>
                </TooltipContent>
              )}
            </Tooltip>

            <Separator className="my-2" />

            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button 
                    onClick={onAddInternet}
                    variant="ghost" 
                    className={cn(
                      "w-full justify-start h-8 text-sm",
                      !canAddInternet && "opacity-50 cursor-not-allowed"
                    )}
                    size="sm"
                    disabled={!canAddInternet}
                  >
                    <Globe className="h-4 w-4 mr-2 text-cyan-500" />
                    Add Internet
                    {canAddInternet && <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">✓</Badge>}
                  </Button>
                </div>
              </TooltipTrigger>
              {!canAddInternet && (
                <TooltipContent side="right">
                  <p>Select a Site, Zone, or System</p>
                </TooltipContent>
              )}
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button 
                    onClick={onAddDMZ}
                    variant="ghost" 
                    className={cn(
                      "w-full justify-start h-8 text-sm",
                      !canAddDMZ && "opacity-50 cursor-not-allowed"
                    )}
                    size="sm"
                    disabled={!canAddDMZ}
                  >
                    <Shield className="h-4 w-4 mr-2 text-red-500" />
                    Add DMZ / Firewall
                    {canAddDMZ && <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">✓</Badge>}
                  </Button>
                </div>
              </TooltipTrigger>
              {!canAddDMZ && (
                <TooltipContent side="right">
                  <p>Select a Site, Zone, System, or Cloud Service</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <Separator />

      {/* Layout Actions */}
      <div>
        <h3 className="font-semibold text-xs uppercase text-muted-foreground mb-2">Layout</h3>
        <div className="space-y-1">
          <Button 
            onClick={onGenerateFromBrowser}
            disabled={isLayouting}
            variant="outline" 
            className="w-full justify-start h-8 text-sm"
            size="sm"
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", isLayouting && "animate-spin")} />
            {isLayouting ? 'Generating...' : 'Generate from Browser'}
          </Button>
          
          <Button 
            onClick={onDeleteEdge}
            variant="ghost"
            className={cn(
              "w-full justify-start h-8 text-sm",
              !selectedEdgeId && "opacity-50 cursor-not-allowed"
            )}
            size="sm"
            disabled={!selectedEdgeId}
          >
            <Trash2 className="h-4 w-4 mr-2 text-destructive" />
            Delete Connection
          </Button>
          
          <Button 
            onClick={onFitView}
            variant="ghost" 
            className="w-full justify-start h-8 text-sm"
            size="sm"
          >
            <Maximize className="h-4 w-4 mr-2" />
            Fit to View
          </Button>
        </div>
      </div>

      <Separator />

      {/* Export */}
      <div>
        <h3 className="font-semibold text-xs uppercase text-muted-foreground mb-2">Export</h3>
        <div className="space-y-1">
          <Button 
            onClick={onExportPNG}
            variant="ghost" 
            className="w-full justify-start h-8 text-sm"
            size="sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Export PNG
          </Button>
        </div>
      </div>

      <Separator />

      {/* Legend */}
      <div>
        <h3 className="font-semibold text-xs uppercase text-muted-foreground mb-2">Legend</h3>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded border-2 border-slate-400 bg-slate-100 dark:bg-slate-800"></div>
            <span>Site (Physical Location)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded border-2 border-blue-500 bg-blue-100 dark:bg-blue-900"></div>
            <span>IT Domain</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded border-2 border-orange-500 bg-orange-100 dark:bg-orange-900"></div>
            <span>OT Domain</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded border-2 border-purple-500 bg-purple-100 dark:bg-purple-900"></div>
            <span>Cloud Domain</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded bg-gradient-to-r from-red-200 to-rose-300 dark:from-red-800 dark:to-rose-900 border-l-2 border-red-500"></div>
            <span>DMZ / Firewall</span>
          </div>
        </div>
      </div>
    </div>
  );
}
